// src/pages/SubGroup.jsx
import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Snackbar,
  TextField,
} from "@mui/material";
import { FaFilter } from "react-icons/fa";
import { useLocation } from "react-router-dom";

const SubGroup = () => {
  const { state } = useLocation();
  const email = state?.email;
  const customerId = state?.customerId;
  const groupId = state?.groupId;

  const [data, setData] = useState([]);
  const [showFilter, setShowFilter] = useState(false);
  const [filterText, setFilterText] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarError, setSnackbarError] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedCampaigns, setSelectedCampaigns] = useState([]);
  const [categoryName, setCategoryName] = useState("");
  const [creating, setCreating] = useState(false);

  const fetchMetrics = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        "https://api.confidanto.com/sub-channel/fetch-sub-channels",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, customer_id: customerId, group_id: groupId }),
        }
      );

      const result = await response.json();
      if (!Array.isArray(result?.sub_channels)) throw new Error("Invalid response");

      const campaignData = [];

      for (const sub of result.sub_channels) {
        try {
          const res = await fetch(
            "https://api.confidanto.com/sub-channel/fetch-sub-channel-by-id",
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                email,
                customer_id: customerId,
                sub_channel_id: sub.sub_channel_id,
              }),
            }
          );

          if (!res.ok) continue;
          const detail = await res.json();
          campaignData.push(...(Array.isArray(detail) ? detail : []));
        } catch (e) {
          console.error("Detail fetch error:", e);
        }
      }

      setData(campaignData);
    } catch (error) {
      console.error("Fetch error:", error);
      setSnackbarError("Failed to fetch campaign data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (email && customerId && groupId) fetchMetrics();
  }, [email, customerId, groupId]);

  useEffect(() => {
    if (!filterText.trim()) {
      setFilteredData(data);
    } else {
      const lower = filterText.toLowerCase();
      const updated = data
        .map((sub) => {
          const filteredIds = [];
          const filteredNames = [];
          sub.campaign_names.forEach((name, idx) => {
            if (name.toLowerCase().includes(lower)) {
              filteredNames.push(name);
              filteredIds.push(sub.campaign_ids[idx]);
            }
          });
          return {
            ...sub,
            campaign_ids: filteredIds,
            campaign_names: filteredNames,
          };
        })
        .filter((s) => s.campaign_ids.length > 0);

      setFilteredData(updated);
    }
  }, [filterText, data]);

  const handleSelectAll = (ids) => {
    const allSelected = ids.every((id) => selectedCampaigns.includes(id));
    setSelectedCampaigns((prev) =>
      allSelected ? prev.filter((id) => !ids.includes(id)) : [...new Set([...prev, ...ids])]
    );
  };

  const handleRowSelect = (id) => {
    setSelectedCampaigns((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleCreateCategory = async () => {
    if (!categoryName.trim()) return alert("Enter a category name");

    const selectedData = [];

    data.forEach((sub) => {
      sub.campaign_ids.forEach((id, idx) => {
        if (selectedCampaigns.includes(id)) {
          selectedData.push({
            id,
            name: sub.campaign_names[idx],
            sub_channel_id: sub.sub_channel_id,
            group_id: groupId,
            customer_id: customerId,
            email,
          });
        }
      });
    });

    if (selectedData.length < 2) {
      alert("Select at least 2 campaigns.");
      return;
    }

    const payload = {
      category_name: categoryName,
      customer_id: customerId,
      campaign_ids: selectedData.map((c) => c.id),
      campaign_names: selectedData.map((c) => c.name),
      number_of_campaigns: selectedData.length,
      email,
      group_id: groupId,
      sub_channel_id: selectedData[0].sub_channel_id,
    };

    try {
      setCreating(true);
      const res = await fetch("https://api.confidanto.com/category/create", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const result = await res.json();
      if (res.ok) {
        setSnackbarOpen(true);
        alert(result.message || "Category created successfully");
        setCategoryName("");
        setSelectedCampaigns([]);
      } else {
        setSnackbarError(result.message || "Failed to create category");
      }
    } catch (err) {
      console.error("Category creation error:", err);
      setSnackbarError("Error creating category");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="p-6">
      {loading ? (
        <div className="text-center">Loading...</div>
      ) : filteredData.length === 0 ? (
        <div className="text-center text-gray-500">No data found.</div>
      ) : (
        filteredData.map((sub, index) => (
          <div key={index} className="mb-8">
            <h2 className="text-lg font-semibold mb-2">
              Sub-channel: <span className="text-blue-600">{sub.sub_channel_name}</span>
            </h2>

            {selectedCampaigns.length > 0 && (
              <div className="flex gap-4 mt-4">
                <TextField
                  label="Category Name"
                  value={categoryName}
                  onChange={(e) => setCategoryName(e.target.value)}
                  sx={{ width: 300 }}
                />
                <Button
                  variant="contained"
                  onClick={handleCreateCategory}
                  disabled={!categoryName || creating}
                >
                  {creating ? "Creating..." : "Create Category"}
                </Button>
              </div>
            )}

            <div className="mt-4">
              <button
                onClick={() => setShowFilter((prev) => !prev)}
                className="hover:bg-slate-200 p-2 rounded text-gray-600 flex items-center"
              >
                <FaFilter className="inline mr-1" /> Add filter
              </button>
              {showFilter && (
                <div className="mt-2">
                  <TextField
                    label="Filter by Campaign Name"
                    value={filterText}
                    onChange={(e) => setFilterText(e.target.value)}
                    size="small"
                    sx={{ width: 300 }}
                  />
                </div>
              )}
            </div>

            <div className="overflow-x-auto border border-gray-300 rounded-lg mt-4">
              <table className="min-w-full text-sm">
                <thead className="bg-[#2930a8] text-white">
                  <tr>
                    <th className="p-2 text-left">
                      <Checkbox
                        sx={{ color: "white" }}
                        checked={sub.campaign_ids.every((id) =>
                          selectedCampaigns.includes(id)
                        )}
                        indeterminate={
                          sub.campaign_ids.some((id) =>
                            selectedCampaigns.includes(id)
                          ) &&
                          !sub.campaign_ids.every((id) =>
                            selectedCampaigns.includes(id)
                          )
                        }
                        onChange={() => handleSelectAll(sub.campaign_ids)}
                      />
                    </th>
                    <th className="p-2 text-left">Campaign ID</th>
                    <th className="p-2 text-left">Campaign Name</th>
                  </tr>
                </thead>
                <tbody>
                  {sub.campaign_ids.map((id, idx) => (
                    <tr
                      key={id}
                      className={`border-t ${
                        selectedCampaigns.includes(id) ? "bg-blue-50" : "bg-white"
                      }`}
                    >
                      <td className="p-2">
                        <Checkbox
                          checked={selectedCampaigns.includes(id)}
                          onChange={() => handleRowSelect(id)}
                        />
                      </td>
                      <td className="p-2">{id}</td>
                      <td className="p-2">{sub.campaign_names[idx]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="text-sm text-gray-500 mt-2">
              Total Campaigns: {sub.number_of_campaigns}
            </div>

            <Snackbar
              open={snackbarOpen}
              autoHideDuration={3000}
              onClose={() => setSnackbarOpen(false)}
            >
              <Alert severity="success" sx={{ width: "100%" }}>
                Category created successfully!
              </Alert>
            </Snackbar>

            <Snackbar
              open={!!snackbarError}
              autoHideDuration={4000}
              onClose={() => setSnackbarError("")}
            >
              <Alert severity="error" sx={{ width: "100%" }}>
                {snackbarError}
              </Alert>
            </Snackbar>
          </div>
        ))
      )}
    </div>
  );
};

export default SubGroup;
